import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { FaPlus, FaUserPlus, FaFileAlt, FaBuilding, FaCalendarAlt, FaCheck } from 'react-icons/fa';
import AuthContext from '../../context/AuthContext';
import RegionalAssessmentTabs from './RegionalAssessmentTabs';
import RegionalTemplateList from './RegionalTemplateList';
import TemplateDetails from './TemplateDetails';
import { groupTemplatesByRegion, getTemplateRegion } from './RegionalTemplateUtils';
import { styles } from './AssessmentModalStyles';

/**
 * Modal component for creating a new assessment
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Function to close the modal
 * @param {string} props.orgId - Selected organization ID (optional)
 * @param {string} props.orgName - Selected organization name (optional)
 */
const NewAssessmentModal = ({ isOpen, onClose, orgId, orgName }) => {
  const authContext = useContext(AuthContext);
  const navigate = useNavigate();
  const { user } = authContext;

  // State variables
  const [step, setStep] = useState(1);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('recommended');
  const [selectedOrgId, setSelectedOrgId] = useState(orgId || '');
  const [selectedOrgName, setSelectedOrgName] = useState(orgName || '');

  // Fetch templates and users on component mount
  useEffect(() => {
    if (isOpen) {
      // Get organization info if not provided as props
      if (!selectedOrgId || !selectedOrgName) {
        getOrganizationInfo();
      }
      fetchTemplates();
    }
  }, [isOpen]);
  
  // Fetch users when organization changes
  useEffect(() => {
    if (selectedOrgId) {
      fetchUsers(selectedOrgId);
    }
  }, [selectedOrgId]);
  
  // Get organization information from localStorage or user context
  const getOrganizationInfo = () => {
    // Try to get organization from localStorage (dashboard state)
    const dashboardState = localStorage.getItem('dashboardState');
    if (dashboardState) {
      try {
        const parsedState = JSON.parse(dashboardState);
        if (parsedState.selectedOrganization && parsedState.selectedOrganization._id) {
          setSelectedOrgId(parsedState.selectedOrganization._id);
          setSelectedOrgName(parsedState.selectedOrganization.name);
          return;
        }
      } catch (error) {
        console.error('Error parsing dashboard state:', error);
      }
    }
    
    // Try to get organization from lastSelectedOrganization
    const lastSelectedOrgData = localStorage.getItem('lastSelectedOrganization');
    if (lastSelectedOrgData) {
      try {
        const lastSelectedOrg = JSON.parse(lastSelectedOrgData);
        setSelectedOrgId(lastSelectedOrg._id);
        setSelectedOrgName(lastSelectedOrg.name);
        return;
      } catch (error) {
        console.error('Error parsing last selected organization:', error);
      }
    }
    
    // Use user's assigned organization as fallback
    if (user && user.organization) {
      const orgId = typeof user.organization === 'object' ? user.organization._id : user.organization;
      const orgName = typeof user.organization === 'object' ? user.organization.name : 'Your Organization';
      setSelectedOrgId(orgId);
      setSelectedOrgName(orgName);
    }
  };

  // Fetch templates from API
  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // ONLY fetch from industry_assessmentquestions collection via industry-templates endpoint
      // This is our single source of truth for templates to avoid duplicates
      console.log('Fetching templates from industry-templates endpoint only');
      const industryTemplatesResponse = await axios.get('/api/assessments/industry-templates', {
        headers: { 'x-auth-token': token }
      });
      
      // Process templates and ensure no duplicates
      const templates = industryTemplatesResponse.data;
      console.log(`Fetched ${templates.length} templates from industry-templates endpoint`);
      
      // Add region to any templates missing it
      templates.forEach(template => {
        if (!template.region) {
          // Determine region based on name
          const name = template.name.toLowerCase();
          if (name.includes('dora')) {
            template.region = 'dora';
          } else if (name.includes('(uk)') || name.includes('fca') || name.includes('pra')) {
            template.region = 'uk';
          } else if (name.includes('(eu)') || name.includes('cca') || name.includes('ai act')) {
            template.region = 'eu';
          } else if (name.includes('(aus)') || name.includes('australia') || name.includes('cirmp')) {
            template.region = 'aus';
          } else {
            template.region = 'general';
          }
        }
      });
      
      // Log template count by region
      console.log('Templates by region:', templates.reduce((acc, t) => {
        acc[t.region] = (acc[t.region] || 0) + 1;
        return acc;
      }, {}));
      
      setTemplates(templates);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching templates:', err);
      setError('Failed to load templates. Please try again.');
      setLoading(false);
    }
  };

  // Fetch users from API filtered by organization
  const fetchUsers = async (orgId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/users', {
        headers: { 'x-auth-token': token }
      });
      
      // Filter users by organization
      const filteredUsers = response.data.filter(user => {
        // Check if user has an organization property
        if (!user.organization) return false;
        
        // Handle both object and string organization references
        const userOrgId = typeof user.organization === 'object' ? 
          user.organization._id : user.organization;
        
        return userOrgId === orgId;
      });
      
      console.log(`Filtered ${filteredUsers.length} users for organization ${orgId}`);
      setUsers(filteredUsers);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users. Please try again.');
    }
  };

  // Handle template selection
  const handleTemplateSelection = (templateId) => {
    setSelectedTemplate(templateId);
  };

  // Handle user selection
  const handleUserSelection = (userId) => {
    if (selectedUsers.includes(userId)) {
      setSelectedUsers(selectedUsers.filter(id => id !== userId));
    } else {
      setSelectedUsers([...selectedUsers, userId]);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (step < 3) {
      // Move to next step if not on final step
      setStep(step + 1);
      return;
    }
    
    // Validate form
    if (!name.trim()) {
      setError('Please enter an assessment name');
      return;
    }
    
    if (!selectedTemplate) {
      setError('Please select a template');
      return;
    }
    
    // Create new assessment
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      // Find the selected template object to determine its industry type
      const selectedTemplateObj = templates.find(t => t._id === selectedTemplate);
      
      // Format the request to match the backend API expectations
      const newAssessment = {
        title: name, // Backend expects 'title' not 'name'
        description,
        templateId: selectedTemplate,
        organizationId: selectedOrgId,
        assignedUsers: selectedUsers.length > 0 ? selectedUsers : [user._id],
        industryType: selectedTemplateObj?.industryType || 'Universal',
        templateName: selectedTemplateObj?.name || `${selectedTemplateObj?.industryType || 'Universal'} Assessment`,
        dueDate: null // Add dueDate field as expected by the backend
      };
      
      console.log('Sending assessment creation request:', newAssessment);
      
      const response = await axios.post('/api/assessments', newAssessment, {
        headers: { 'x-auth-token': token }
      });
      
      console.log('Assessment created successfully:', response.data);
      setLoading(false);
      onClose();
      navigate(`/assessments/${response.data._id}`);
    } catch (err) {
      console.error('Error creating assessment:', err.response?.data || err.message);
      setError(err.response?.data?.msg || 'Failed to create assessment. Please try again.');
      setLoading(false);
    }
  };

  // Handle back button click
  const handleBack = () => {
    setStep(step - 1);
    setError('');
  };

  // Get template details by ID
  const getTemplateDetails = (templateId) => {
    return templates.find(template => template._id === templateId) || null;
  };

  // Group templates by industry for the selected region
  const getGroupedTemplatesByRegion = () => {
    return groupTemplatesByRegion(templates, activeTab);
  };

  // Render step indicator
  const renderStepIndicator = () => {
    return (
      <div style={styles.stepIndicatorContainer}>
        <div style={styles.stepIndicator}>
          <div style={{ ...styles.step, ...(step >= 1 ? styles.activeStep : {}) }}>1</div>
          <div style={{ ...styles.stepLine, ...(step >= 2 ? styles.activeStepLine : {}) }}></div>
          <div style={{ ...styles.step, ...(step >= 2 ? styles.activeStep : {}) }}>2</div>
          <div style={{ ...styles.stepLine, ...(step >= 3 ? styles.activeStepLine : {}) }}></div>
          <div style={{ ...styles.step, ...(step >= 3 ? styles.activeStep : {}) }}>3</div>
        </div>
      </div>
    );
  };

  // Render navigation buttons
  const renderNavButtons = () => {
    return (
      <div style={styles.buttonContainer}>
        {step > 1 && (
          <button
            type="button"
            onClick={handleBack}
            style={{ ...styles.button, ...styles.secondaryButton }}
          >
            Back
          </button>
        )}
        
        <button
          type="submit"
          style={{ ...styles.button, ...styles.primaryButton, marginLeft: 'auto' }}
          disabled={loading}
        >
          {step === 3 ? 'Create Assessment' : 'Next'}
        </button>
      </div>
    );
  };

  // Render step 1: Basic information
  const renderStep1 = () => {
    return (
      <div>
        <div style={styles.formGroup}>
          <label style={styles.label}>Assessment Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter assessment name"
            style={styles.input}
            required
          />
        </div>
        
        <div style={styles.formGroup}>
          <label style={styles.label}>Description (Optional)</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Enter assessment description"
            style={{ ...styles.input, minHeight: '100px' }}
          />
        </div>
        
        <div style={styles.formGroup}>
          <label style={styles.label}>Organization</label>
          <input
            type="text"
            value={selectedOrgName || 'Default Organization'}
            style={{ ...styles.input, backgroundColor: '#f5f5f5' }}
            disabled
          />
          <p>This assessment will be created for <span style={{ fontWeight: 'bold', color: '#243444' }}>{selectedOrgName}</span></p>
        </div>
      </div>
    );
  };

  // Render step 2: Template selection
  const renderStep2 = () => {
    const groupedTemplates = getGroupedTemplatesByRegion();
    const selectedTemplateDetails = getTemplateDetails(selectedTemplate);
    
    return (
      <div>
        <div style={styles.formGroup}>
          <label style={styles.label}>Select Assessment Template</label>
          <p>Choose a template for your assessment:</p>
          
          {/* Tabs for template regions */}
          <RegionalAssessmentTabs 
            activeTab={activeTab} 
            setActiveTab={setActiveTab} 
            styles={styles} 
          />
          
          {/* Template selection container */}
          <div style={styles.templateSelectionContainer}>
            <RegionalTemplateList 
              groupedTemplates={groupedTemplates}
              activeTab={activeTab}
              selectedTemplate={selectedTemplate}
              handleTemplateSelection={handleTemplateSelection}
              styles={styles}
            />
            
            <TemplateDetails 
              templateDetails={selectedTemplateDetails}
              activeTab={activeTab}
              styles={styles}
            />
          </div>
        </div>
      </div>
    );
  };

  // Render step 3: User assignment
  const renderStep3 = () => {
    return (
      <div>
        <div style={styles.formGroup}>
          <label style={styles.label}>Assign Users (Optional)</label>
          <p>Select users to assign to this assessment:</p>
          
          <div style={styles.userSelectionContainer}>
            {users.length > 0 ? (
              users.map(u => (
                <div
                  key={u._id}
                  style={{
                    ...styles.userCard,
                    ...(selectedUsers.includes(u._id) ? styles.selectedUserCard : {})
                  }}
                  onClick={() => handleUserSelection(u._id)}
                >
                  <div style={styles.userName}>{u.name}</div>
                  <div style={styles.userEmail}>{u.email}</div>
                  {u._id === user._id && (
                    <div style={{ fontSize: '12px', color: '#D2BA7B', marginTop: '5px' }}>You</div>
                  )}
                </div>
              ))
            ) : (
              <div style={{ padding: '20px', textAlign: 'center', color: '#4C5454' }}>
                No users found for this organization.
              </div>
            )}
          </div>
          
          <p style={{ marginTop: '10px', fontSize: '14px', color: '#4C5454' }}>
            {selectedUsers.length === 0 ? 
              'If no users are selected, you will be assigned by default.' : 
              `${selectedUsers.length} user(s) selected`}
          </p>
        </div>
      </div>
    );
  };

  // If modal is not open, don't render anything
  if (!isOpen) return null;

  // Modal component
  return (
    <div style={styles.modal}>
      <div style={styles.modalContent}>
        <button style={styles.closeButton} onClick={onClose}>×</button>
        <h2 style={{ color: '#243444', marginTop: 0 }}>Create New Assessment</h2>
        
        {loading ? (
          <div style={styles.loading}>Loading...</div>
        ) : (
          <form style={styles.form} onSubmit={handleSubmit}>
            {error && <div style={styles.error}>{error}</div>}
            
            {renderStepIndicator()}
            
            {step === 1 && renderStep1()}
            {step === 2 && renderStep2()}
            {step === 3 && renderStep3()}
            
            {renderNavButtons()}
          </form>
        )}
      </div>
    </div>
  );
};

export default NewAssessmentModal;
